import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ResumeItem from '@/common/components/ResumeItem'
import Table from '@/common/components/Table'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { formatCurrency } from '@/common/utils/formatters'
import { useClientCompanyPaymentMethods } from '../../hooks/useClientCompanyPaymentMethods'
import { ClientPaymentMethodData } from '../../types/paymentMethods.type'
import { useBankingDeposits } from '../../hooks/useBankingDeposits'
import { BankingDepositsTransformer } from '../../services/bankingDepositsTransformer'
import { bankingDepositStatusOptions } from '../../constants/bankingDepositStatus.constant'
import { bankingDepositsTableColumns } from '../../constants/bankingDepositsTableColumns.constant'
import { useMineCompany } from '@/features/company/hooks/useMineCompany'
import ListHeader from '@/common/components/ListHeader'
import { FileInputIcon } from 'lucide-react'
import useCompanyRemainingCreditAndBalance from '@/common/hooks/queries/useCompanyRemainingCreditAndBalance'
import useUserPermissions from '@/common/hooks/useUserPermissions'

const BankingDepositsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const { date, formattedDate } = useFilterDate(filterParams);

  const {
    data: company,
    isPending: isCompanyPending,
  } = useMineCompany();

  const {
    data: companyRemainingCreditAndBalance,
    isPending: isCompanyRemainingCreditAndBalancePending,
  } = useCompanyRemainingCreditAndBalance();

  const { hasUserPermission } = useUserPermissions();

  const hasUserPermissionToGetCompanyRemainingCreditAndBalance = hasUserPermission("Client::Companies::RemainingCreditAndBalance");

  const {
    data: paymentMethodsOptions,
  } = useClientCompanyPaymentMethods({
    mapper: Mapper.mapToOptions<ClientPaymentMethodData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const {
    data: bankingDeposits,
    isPending: isBankingDepositsPending,
    error: bankingDepositsError = {} as any,
    refetch: refetchBankingDeposits,
    meta: bankingDepositMeta,
  } = useBankingDeposits({
    filters: {
      status: filterParams.status?.value,
      page: Number(filterParams.page) || 1,
      paymentMethod: filterParams?.paymentMethod?.value,
      createdAtGt: formattedDate.from,
      createdAtLt: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer({ transformer: BankingDepositsTransformer.tableTransformer() }),
  });

  return (
    <ErrorMessageWrapper
      statusCode={(company?.kind === "private_company" || isCompanyPending) ? 200 : 404}
      notFoundHideButton
    >
      <div className='flex flex-col gap-6'>
        <ListHeader
          Icon={<FileInputIcon className="w-10 h-10" strokeWidth={1} />}
          title="Depósitos"
          createButtonText={company?.kind === "private_company" ? "Adicionar Saldo (Pré-pago)" : undefined}
          operationKlassToCheckPermission="Client::Banking::Deposits::Create"
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-192px)]"
          statusCode={bankingDepositsError?.status}
          refetchFunction={refetchBankingDeposits}
        >
          <div className="flex flex-col gap-6">
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <DatePickerWithRange
                  date={date}
                  setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
                  placeholder='Calendário'
                />

                <ListFilters
                  filters={[
                    {
                      className: '',
                      placeholder: 'Status',
                      options: [{ label: 'Todos', value: '' }, ...bankingDepositStatusOptions],
                      onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                      value: filterParams.status,
                    },
                    {
                      className: '',
                      placeholder: 'Via',
                      options: [{ label: 'Todos', value: '' }, ...paymentMethodsOptions],
                      onChange: (paymentMethod) => setFilterParams((prev) => ({ ...prev, paymentMethod })),
                      value: filterParams.paymentMethod,
                    },
                  ]}
                />
              </div>

              {hasUserPermissionToGetCompanyRemainingCreditAndBalance && (
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-4'>
                    <ResumeItem
                      label='Limite disponível (Pós-pago)'
                      value={companyRemainingCreditAndBalance?.remaining_credit
                        ? formatCurrency(Number(companyRemainingCreditAndBalance?.remaining_credit))
                        : '--'}
                      isLoading={isCompanyRemainingCreditAndBalancePending}
                    />

                    <ResumeItem
                      label='Saldo disponível (Pré-pago)'
                      value={companyRemainingCreditAndBalance?.remaining_balance
                        ? formatCurrency(Number(companyRemainingCreditAndBalance?.remaining_balance))
                        : '--'}
                      valueClassName='text-valid'
                      isLoading={isCompanyRemainingCreditAndBalancePending}
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Table
                className="max-h-[calc(100vh-220px)]"
                columns={bankingDepositsTableColumns}
                isLoadingData={isBankingDepositsPending}
                rows={bankingDeposits}
                totalPages={bankingDepositMeta.total_pages}
                totalCount={bankingDepositMeta.total}
                hasPagination
                page={filterParams.page || 1}
                setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
                rowEmptyValue="--"
              />
            </div>
          </div>
        </ErrorMessageWrapper>
      </div>
    </ErrorMessageWrapper>
  )
}

export default BankingDepositsList
